import type { StyleSystemProps } from '@withjoy/joykit';
import { Box, ButtonV2, css, Flex, styled, TextV2 } from '@withjoy/joykit';
import type { ButtonOverrides } from '@withjoy/joykit/components/ButtonV2/Button.types';
import type { ProductTileProps } from '../../ProductTile.types';
import { pxToRem } from '@withjoy/joykit/theme';

const DEFAULT_IMAGE = 'https://withjoy.com/assets/public/apps/registry/custom-item-default.svg';

export const StyledProductPhoto = styled.div.attrs<{ photoUrl?: string }>(({ photoUrl }) => {
  return {
    style: {
      backgroundImage: photoUrl ? `url("${photoUrl}")` : `url("${DEFAULT_IMAGE}")`,
      backgroundSize: photoUrl ? 'contain' : '50%',
    },
  };
})<{ photoUrl?: string }>`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  mix-blend-mode: multiply;
  background-position: center;
  transition: transform 0.25s;
  justify-content: center;
  display: flex;
  transform: scale(1.0001);
  position: absolute;
  top: 0;
`;

export const StyledProductTile = styled(Box)`
  contain: content;
  width: 100%;
  position: relative;
  @media (hover: hover) {
    :hover {
      position: relative;
    }
  }
`;

export const StyledFlex = styled(Flex)``;

export const ProductTitleText = styled(TextV2)<{ $variant: ProductTileProps['variant'] }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: ${props => (props.$variant === 'gridItem' ? 'center' : 'start')};

  ${props => props.theme.mediaQueries.down({ viewport: 'sm' })} {
    -webkit-line-clamp: 3;
  }
`;

export const StyledQuickAddButton = styled(ButtonV2)<{ loading: boolean; $darkBg: boolean }>`
  height: pxToRem(48);
  ${({ $darkBg = false }) =>
    $darkBg
      ? css`
          background-color: transparent;
          color: white;
        `
      : css`
          color: #4751f8;
          svg {
            color: #4751f8;
          }
          &:hover {
            color: #4751f8;
            text-decoration-color: #4751f8;
          }
        `}

  svg {
    width: 22px;
    height: 22px;
  }
`;

const buttonTextStyles: StyleSystemProps = {
  color: 'linkText',
  transition: 'color 500ms cubic-bezier(0.23, 1, 0.32, 1) 0s',
  display: 'flex',
};

const brandTextStyles: StyleSystemProps = {
  minHeight: '19px',
  letterSpacing: '-0.015em',
  marginBottom: 1,
  color: 'black',
  textAlign: 'center',
};

const brandTextDarkBgStyles: StyleSystemProps = {
  ...brandTextStyles,
  color: 'white',
};

const productTitleStyles: StyleSystemProps = {
  maxWidth: pxToRem(288),
  minHeight: 'calc(1rem * 1.5 * 2)', // two full lines; somehow, it's not 1.4 line-height
  fontSize: '1rem',
  lineHeight: 1.4,
  marginBottom: 1,
  whiteSpace: 'normal',
};

const productTitleDarkBgStyles: StyleSystemProps = {
  ...productTitleStyles,
  color: 'white',
};

const photoWrapperStyles: StyleSystemProps = {
  overflow: 'hidden',
  borderRadius: '4px',
  position: 'relative',
  width: '100%',
  paddingTop: '100%',
};

const flexTextWrapperStyles: StyleSystemProps = {
  paddingTop: 0,
};

const storefrontHeadContainerStyles: StyleSystemProps = {
  justifyContent: 'center',
  width: '100%',
  minHeight: '37px',
};

export const quickAddButtonOverrides: ButtonOverrides = {
  Root: {
    props: {
      color: 'mono14',
      background: 'white',
      _hover: {
        // eslint-disable-next-line
        color: 'mono14',
      },
      _active: {
        // eslint-disable-next-line
        color: 'mono14',
      },
    },
  },
};
export const hoveredColorSwatchStyles: StyleSystemProps = {
  zIndex: 'hide',
};

export const StyledIcon = styled(Box)`
  position: relative;
  margin: 12px;
  width: 24px;
  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.mono6};
  }
`;

export const styles = {
  brandText: brandTextStyles,
  brandTextDarkBg: brandTextDarkBgStyles,
  buttonText: buttonTextStyles,
  productTitle: productTitleStyles,
  productTitleDarkBg: productTitleDarkBgStyles,
  photoWrapper: photoWrapperStyles,
  flexTextWrapper: flexTextWrapperStyles,
  hovered: hoveredColorSwatchStyles,
  storefrontHeadContainer: storefrontHeadContainerStyles,
};
